/**
 * Single source of truth for Oshar Real Estate property card UI + checkout gates.
 */

function tierSlugOf(asset) {
  return String(asset?.tierSlug || asset?.id || "").trim();
}

function cardRemaining(asset, tierSource) {
  if (tierSource === "fallback" || tierSource === "loading") return null;
  if (asset.remaining != null && Number.isFinite(Number(asset.remaining))) {
    return Math.max(0, Number(asset.remaining));
  }
  return Math.max(0, Number(asset.supply) || 0);
}

function resolveChainStatusLabel({ minted, statusOverride }) {
  if (minted) return "Minted";
  if (statusOverride === "reserved") return "Reserved";
  if (statusOverride === "unavailable") return "Unavailable";
  return "Available";
}

function cardMaxPerCheckout(asset, remaining) {
  if (asset.maxPerCheckout != null && Number.isFinite(Number(asset.maxPerCheckout))) {
    return Math.max(1, Math.floor(Number(asset.maxPerCheckout)));
  }
  return Math.max(1, Math.min(50, remaining || 1));
}

/**
 * @param {object} params
 * @param {object} params.asset — tier row (API-mapped or static fallback)
 * @param {"api"|"fallback"|"loading"} params.tierSource
 * @param {{ minted?: boolean, checking?: boolean, owner?: string }} [params.chainStatus]
 * @param {boolean} [params.stripeFulfillmentReady]
 * @param {boolean} [params.tokenPresent]
 */
export function resolveOsharRealEstateCardState({
  asset,
  tierSource = "api",
  chainStatus = { minted: false, checking: false, owner: "" },
  stripeFulfillmentReady = false,
  tokenPresent = false,
} = {}) {
  const slug = tierSlugOf(asset);
  const statusOverride = asset?.statusOverride || null;
  const displayStatus = resolveChainStatusLabel({
    minted: Boolean(chainStatus?.minted),
    statusOverride,
  });

  const remaining = cardRemaining(asset, tierSource);
  const supplyFloor = Math.max(0, Number(asset?.supply) || 0);
  const stock = remaining != null ? remaining : supplyFloor;
  const maxSel = Math.max(1, Math.min(cardMaxPerCheckout(asset, stock), stock || 1));

  const pricePending =
    asset?.priceLabel === "TBD" ||
    Boolean(asset?.priceUsdNeedsConfirmation || asset?.needsConfirmation);
  const registryOffline = tierSource === "fallback";
  const catalogLoading = tierSource === "loading";
  const soldOut = !registryOffline && !catalogLoading && stock < 1;
  const checkoutExplicitlyOff = asset?.checkoutEnabled === false;
  const tierReserved = statusOverride === "reserved" || displayStatus === "Reserved";
  const tierUnavailable = statusOverride === "unavailable" || displayStatus === "Unavailable";
  const tierMinted = displayStatus === "Minted";

  let status = "available";
  let badgeLabel = displayStatus;
  let primaryMessage = "";

  if (catalogLoading) {
    status = "loading";
    badgeLabel = "Loading Inventory";
    primaryMessage = "Loading live tier inventory from the Treydora API…";
  } else if (registryOffline) {
    status = "registry_offline";
    badgeLabel = displayStatus === "Available" ? "Catalog Preview" : displayStatus;
    primaryMessage =
      "Live tier inventory is unavailable. Card checkout is disabled until GET /api/oshar-real-estate/tiers succeeds.";
  } else if (tierMinted) {
    status = "sold_out";
    badgeLabel = "Minted";
    primaryMessage = "This representative on-chain token is already minted.";
  } else if (tierReserved) {
    status = "reserved";
    badgeLabel = "Reserved";
    primaryMessage = asset?.reservationNote || "This property tier is currently reserved.";
  } else if (tierUnavailable) {
    status = "unavailable";
    badgeLabel = "Unavailable";
    primaryMessage = asset?.disabledReason || "This property tier is currently unavailable.";
  } else if (pricePending) {
    status = "price_pending";
    badgeLabel = "Token Price Setup Pending";
    primaryMessage =
      asset?.disabledReason ||
      "On-chain token pricing is not configured yet. Wallet mint is disabled until setup completes.";
  } else if (soldOut) {
    status = "sold_out";
    badgeLabel = "Sold Out";
    primaryMessage = "No remaining inventory for this tier.";
  } else if (checkoutExplicitlyOff) {
    status = "not_configured";
    badgeLabel = "Unavailable";
    primaryMessage = asset?.disabledReason || "This property tier is not offered for checkout.";
  } else if (!slug) {
    status = "not_configured";
    badgeLabel = "Unavailable";
    primaryMessage = "This property tier is not configured.";
  }

  const cardPriceReady =
    asset?.checkoutEnabled === true ||
    (!pricePending && (Number(asset?.price) > 0 || asset?.usdcPrice != null));
  const stripeReady =
    !registryOffline &&
    !catalogLoading &&
    cardPriceReady &&
    !tierReserved &&
    !tierUnavailable &&
    !tierMinted &&
    !soldOut &&
    !checkoutExplicitlyOff &&
    (asset?.checkoutEnabled === true ||
      stripeFulfillmentReady === true ||
      asset?.stripeFulfillmentReady === true);

  const walletReady =
    !registryOffline &&
    !catalogLoading &&
    !pricePending &&
    !tierReserved &&
    !tierUnavailable &&
    !tierMinted &&
    !soldOut &&
    !checkoutExplicitlyOff &&
    Boolean(slug) &&
    stock >= 1;

  const canShowQuantity = (stripeReady || walletReady) && stock >= 1;
  const canShowTerms = stripeReady || walletReady;
  const canUseWallet = walletReady;
  const canUseCard = stripeReady;

  let cardButtonLabel = "Pay with Card";
  if (catalogLoading) cardButtonLabel = "Loading Inventory…";
  else if (registryOffline) cardButtonLabel = "Checkout Unavailable";
  else if (tierReserved) cardButtonLabel = "Reserved";
  else if (tierUnavailable || checkoutExplicitlyOff) cardButtonLabel = "Unavailable";
  else if (tierMinted) cardButtonLabel = "Minted";
  else if (soldOut) cardButtonLabel = "Sold Out";
  else if (pricePending && !stripeReady) cardButtonLabel = "Token Price Setup Pending";
  else if (!tokenPresent) cardButtonLabel = "Log in to Pay with Card";
  else if (!canUseCard) cardButtonLabel = "Card Checkout Unavailable";

  let disabledReason = primaryMessage;
  if (canUseCard || canUseWallet) disabledReason = "";
  else if (!disabledReason) {
    disabledReason = "This property tier is unavailable for checkout.";
  }

  return {
    status,
    badgeLabel,
    primaryMessage,
    displayStatus,
    canShowQuantity,
    canShowTerms,
    canUseWallet,
    canUseCard,
    walletButtonLabel: null,
    cardButtonLabel,
    disabledReason,
    _stock: stock,
    _remaining: remaining,
    _maxSel: maxSel,
    tierSlug: slug,
    pricePending,
    registryOffline,
  };
}

/**
 * Step-based wallet CTA label (PropertyTypeGrid).
 */
export function resolveOsharWalletButtonLabel({
  cardState,
  hasInjectedWallet,
  isConnected,
  wrongNetwork,
  legalComplete,
  walletPhase,
  walletLoadingId,
  tierCardId,
  walletDashboardSyncPending,
  isConnectingWallet,
  isSwitchingNetwork,
  switchNetworkLabel,
}) {
  if (walletDashboardSyncPending) {
    return walletLoadingId === tierCardId ? "Syncing to Dashboard…" : "Mint succeeded — Retry Dashboard Sync";
  }
  if (isConnectingWallet) return "Connecting wallet…";
  if (isSwitchingNetwork) return "Confirm in MetaMask";
  if (walletLoadingId === tierCardId) {
    const ph = walletPhase;
    if (ph?.stage === "preparing") return "Preparing Mint…";
    if (ph?.stage === "minting") return "Confirm in MetaMask";
    if (ph?.stage === "syncing") return "Syncing to Dashboard…";
    if (ph?.stage === "sync_pending") return "Mint succeeded — Retry Dashboard Sync";
    return "Preparing Mint…";
  }
  if (walletPhase?.stage === "done") return "View in Dashboard";
  if (!cardState?.canUseWallet) {
    if (cardState?.registryOffline) return "Checkout Unavailable";
    if (cardState?.status === "loading") return "Loading Inventory…";
    if (cardState?.pricePending) return "Token Price Setup Pending";
    if (cardState?.status === "reserved") return "Reserved";
    if (cardState?.status === "unavailable" || cardState?.status === "not_configured") return "Unavailable";
    if (cardState?.status === "sold_out") return "Sold Out";
    return "Wallet Mint Unavailable";
  }
  if (!hasInjectedWallet) return "Install/Open MetaMask";
  if (!isConnected) return "Connect Wallet";
  if (wrongNetwork) return switchNetworkLabel || "Switch Network";
  if (!legalComplete) return "Accept Terms to Continue";
  return "Mint with Wallet";
}

/** True only when an explicit debug query flag is present (never from NODE_ENV alone). */
export function hasOsharWalletDebugFlags(searchParams) {
  const sp = searchParams || (typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null);
  if (!sp) return false;
  return sp.get("debugPurchase") === "1" || sp.get("debugOshar") === "1";
}

/** @deprecated Use hasOsharWalletDebugFlags */
export function shouldShowOsharWalletDiagnostics(searchParams) {
  return hasOsharWalletDebugFlags(searchParams);
}

/**
 * Wallet CTA step — must match the rendered button label in PropertyTypeGrid.
 * @returns {"blocked"|"install"|"connect"|"switch_network"|"accept_terms"|"mint"|"retry_sync"|"view_dashboard"|"mint_busy"}
 */
export function resolveOsharWalletStep({
  cardState,
  hasInjectedWallet,
  isConnected,
  wrongNetwork,
  legalComplete,
  walletPhase,
  walletLoadingId,
  tierCardId,
  walletDashboardSyncPending,
  isConnectingWallet,
  isSwitchingNetwork,
}) {
  if (walletDashboardSyncPending) {
    return walletLoadingId === tierCardId ? "mint_busy" : "retry_sync";
  }
  if (isConnectingWallet) return "mint_busy";
  if (isSwitchingNetwork) return "mint_busy";
  if (walletLoadingId === tierCardId) return "mint_busy";
  if (walletPhase?.stage === "done") return "view_dashboard";
  if (!cardState?.canUseWallet) return "blocked";
  if (!hasInjectedWallet) return "install";
  if (!isConnected) return "connect";
  if (wrongNetwork) return "switch_network";
  if (!legalComplete) return "accept_terms";
  return "mint";
}
